import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, RefreshCw } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, formatApiErrorDetail } from "@/lib/api";

// Repost an expired ad: shows the posting fee (if any) and sends the ad back to admin review.
export function RepostDialog({ ad, onClose, onDone, L }) {
  const [quote, setQuote] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!ad) return;
    setQuote(null);
    api.get(`/listings/${ad.id}/repost-quote`).then(({ data }) => setQuote(data)).catch(() => setQuote({ fee: 0, days: 30 }));
  }, [ad]);
  const confirm = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/listings/${ad.id}/repost`);
      toast.success(data.fee > 0
        ? L(`تم إرسال الإعلان للإدارة للاعتماد. بعد الموافقة ادفع رسوم النشر ${data.fee} درهم إماراتي ليظهر الإعلان`, `Ad sent to the admin for approval. After approval, pay the ${data.fee} AED posting fee to publish it`)
        : L("تم إرسال الإعلان إلى الإدارة للتدقيق والاعتماد", "Your ad has been sent to the admin for review and approval"));
      onDone(); onClose();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={!!ad} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-2xl" data-testid="repost-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><RefreshCw className="w-5 h-5 text-[#1A5C3A]" />{L("إعادة نشر الإعلان", "Repost ad")}</DialogTitle></DialogHeader>
        {!quote ? <Loader2 className="w-6 h-6 animate-spin mx-auto text-[#1A5C3A]" /> : (
          <div className="space-y-3 text-sm">
            <p>{L(`سيُعاد نشر «${ad?.title}» كما هو لمدة ${quote.days} يوماً بعد اعتماد الإدارة.`, `"${ad?.title}" will be reposted as is for ${quote.days} days after admin approval.`)}</p>
            {quote.fee > 0 ? (
              <div className="rounded-xl border-2 border-amber-400 bg-amber-50 p-3 font-bold text-amber-900" data-testid="repost-fee">
                {L("رسوم النشر لهذا القسم", "Posting fee for this category")}: <span className="text-[#1A5C3A]" dir="ltr">{quote.fee} {L("درهم إماراتي", "AED")}</span>
                <p className="font-normal text-xs mt-1">{L("تُدفع بعد موافقة الإدارة، ولن يظهر الإعلان إلا بعد الدفع.", "Paid after admin approval; the ad goes live only after payment.")}</p>
              </div>
            ) : <p className="font-bold text-[#1A5C3A]" data-testid="repost-free">{L("إعادة النشر مجانية لهذا القسم", "Reposting is free for this category")}</p>}
            <button onClick={confirm} disabled={busy} className="w-full h-11 rounded-xl bg-[#064E3B] text-white font-bold flex items-center justify-center gap-2 disabled:opacity-60" data-testid="repost-confirm">
              {busy && <Loader2 className="w-4 h-4 animate-spin" />}{L("تأكيد إعادة النشر", "Confirm repost")}
            </button>
          </div>
        )}
        <button onClick={onClose} className="w-full h-11 rounded-xl border border-slate-200 font-bold text-slate-700" data-testid="repost-cancel">{L("إلغاء", "Cancel")}</button>
      </DialogContent>
    </Dialog>
  );
}
